import React, { useState, useEffect } from "react";
import { Sparkles, X } from "lucide-react";
import { useToast } from "../../provider/ToastContext"; // adjust path

import UploadSection from "./UploadSection";
import WorkspaceSection from "./WorkspaceSection";

export default function CreateClips({ collapsed }) {
  const { showToast } = useToast();
  const [file, setFile] = useState(null);
  const [videoURL, setVideoURL] = useState("");
  const [loading, setLoading] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [activeTab, setActiveTab] = useState("queue");

  const [selectedJob, setSelectedJob] = useState(null);
  const [clips, setClips] = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);

  const [uploadPreview, setUploadPreview] = useState(null);

  /* ---------------- HELPERS ---------------- */
  const getJobStateUI = (job) => {
    if (!job) return "empty";
    if (job.status === "failed") return "failed";
    if (job.status === "done") return "completed";
    return "processing";
  };

  /* ---------------- FETCH JOBS ---------------- */
  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("http://localhost:8087/api/jobs/list", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!selectedJob) return;

    const updated = jobs.find((j) => j.id === selectedJob.id);
    if (
      updated &&
      (updated.status !== selectedJob.status ||
        updated.progress !== selectedJob.progress ||
        updated.message !== selectedJob.message)
    ) {
      setSelectedJob(updated);
    }
  }, [jobs]);

  /* ---------------- FETCH CLIPS ---------------- */
  const fetchClips = async (videoId) => {
    try {
      setClipsLoading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(
        `http://localhost:8087/api/videos/${videoId}/shorts`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      setClips(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
    setClipsLoading(false);
  };

  /* ---------------- UPLOAD ---------------- */
  const handleFileUpload = async () => {
    if (!file) return;
    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch("http://localhost:8087/api/videos/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();

      if (data.video_id) {
        showToast("AI processing started 🎬", "success");
        setFile(null);
        fetchJobs();
      }
    } catch {
      showToast("Upload failed", "error");
    }

    setLoading(false);
  };

  /* ---------------- URL ---------------- */
  const handleURLSubmit = async () => {
    if (!videoURL.trim()) return;
    showToast("Processing video...", "loading");

    try {
      const token = localStorage.getItem("token");

      const res = await fetch("http://localhost:8087/api/videos/fetchurl", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url: videoURL }),
      });

      const data = await res.json();

      if (data.video_id) {
        showToast("Video queued successfully 🎬", "success");
        setVideoURL("");
        fetchJobs();
      } else {
        showToast("Failed to queue video", "error");
      }
    } catch (err) {
      showToast("Could not import video", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- SELECT JOB ---------------- */
  const handleSelectJob = async (job) => {
    setSelectedJob(job);
    if (job.status === "done") {
      await fetchClips(job.video_id);
    }
  };

  /* ---------------- TIMELINE ---------------- */
  const stages = [
    "video ready",
    "metadata saved",
    "audio extracted",
    "transcription completed",
    "scenes finalized",
    "clips rendered",
  ];

  const renderTimeline = () => {
    if (!selectedJob) return null;

    const currentIndex = stages.indexOf(selectedJob.status);

    return (
      <div className="mt-6 flex flex-col gap-3 text-sm">
        {stages.map((stage, index) => {
          const isDone = currentIndex > -1 && index < currentIndex;
          const isCurrent = index === currentIndex;

          return (
            <div key={stage} className="flex items-center gap-3">
              <div
                className={`w-3 h-3 rounded-full ${
                  isDone
                    ? "bg-green-500"
                    : isCurrent
                      ? "bg-indigo-500 animate-pulse"
                      : "bg-gray-300"
                }`}
              />
              <span className="capitalize">{stage}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 ${
        collapsed ? "lg:ml-20" : "lg:ml-72"
      } p-6 md:p-8 space-y-8`}
    >
      {/* HEADER */}
      <div className="text-center">
        <h1 className="text-3xl font-bold flex justify-center items-center gap-2">
          <Sparkles className="text-indigo-500" />
          AI Shorts Studio
        </h1>
        <p className="text-gray-500 mt-2">
          Upload or import videos to generate viral short clips
        </p>
      </div>

      {/* UPLOAD */}
      <UploadSection
        file={file}
        setFile={setFile}
        videoURL={videoURL}
        setVideoURL={setVideoURL}
        loading={loading}
        handleFileUpload={handleFileUpload}
        handleURLSubmit={handleURLSubmit}
        setUploadPreview={setUploadPreview} // 👈 ADD
      />

      {/* WORKSPACE */}
      <WorkspaceSection
        jobs={jobs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        clips={clips}
        clipsLoading={clipsLoading}
        handleSelectJob={handleSelectJob}
        handleRetry={() => {}}
        renderTimeline={renderTimeline}
        getJobStateUI={getJobStateUI}
      />

      {uploadPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999]">
          {uploadPreview && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999]">
              <div className="relative">
                <button
                  onClick={() => setUploadPreview(null)}
                  className="absolute -top-10 right-0 text-white text-xl"
                >
                  ✕
                </button>

                <div className="w-[320px] h-[568px] bg-black rounded-xl overflow-hidden">
                  <video
                    src={uploadPreview}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
